'use client';

import { useState, useRef, useCallback, useEffect } from 'react';

export interface Position {
  x: number;
  y: number;
}

export interface ImageTransformState {
  position: Position;
  scale: number;
  isDragging: boolean;
}

export interface UseImageTransformOptions {
  initialPosition?: Position;
  initialScale?: number;
  minScale?: number;
  maxScale?: number;
  scaleStep?: number;
  enableZoom?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onScaleChange?: (scale: number) => void;
}

export interface UseImageTransformReturn {
  position: Position;
  scale: number;
  isDragging: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  setPosition: (position: Position) => void;
  setScale: (scale: number) => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
  // Refs for external access in event handlers
  positionRef: React.RefObject<Position>;
  scaleRef: React.RefObject<number>;
  isDraggingRef: React.RefObject<boolean>;
}

const DEFAULT_POSITION: Position = { x: 0, y: 0 };
const DEFAULT_MIN_SCALE = 0.5;
const DEFAULT_MAX_SCALE = 5;
const DEFAULT_SCALE_STEP = 1.25;

export function useImageTransform(
  options: UseImageTransformOptions = {}
): UseImageTransformReturn {
  const {
    initialPosition = DEFAULT_POSITION,
    initialScale = 1,
    minScale = DEFAULT_MIN_SCALE,
    maxScale = DEFAULT_MAX_SCALE,
    scaleStep = DEFAULT_SCALE_STEP,
    enableZoom = true,
    onDragStart,
    onDragEnd,
    onScaleChange,
  } = options;

  const [position, setPosition] = useState<Position>(initialPosition);
  const [scale, setScaleState] = useState(initialScale);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);

  // Refs for accessing current values in event handlers
  const positionRef = useRef(position);
  const scaleRef = useRef(scale);
  const isDraggingRef = useRef(isDragging);
  const dragStartRef = useRef(dragStart);

  // Pinch zoom state
  const pinchStartDistanceRef = useRef<number>(0);
  const pinchStartScaleRef = useRef<number>(1);
  const pinchStartPositionRef = useRef<Position>({ x: 0, y: 0 });
  const pinchMidpointRef = useRef<Position>({ x: 0, y: 0 });

  // Update refs when state changes
  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    dragStartRef.current = dragStart;
  }, [dragStart]);

  const setScale = useCallback(
    (newScale: number) => {
      const clampedScale = Math.min(Math.max(newScale, minScale), maxScale);
      setScaleState(clampedScale);
      onScaleChange?.(clampedScale);
    },
    [minScale, maxScale, onScaleChange]
  );

  // Buttons zoom around the centre of the viewport. The image maps as
  // `position + scale * point`, so holding the centre still means scaling the
  // position by the same factor; leaving it alone slides the image away from
  // whatever the user was looking at.
  const zoomAroundCenter = useCallback(
    (requestedScale: number) => {
      const currentScale = scaleRef.current;
      const newScale = Math.min(Math.max(requestedScale, minScale), maxScale);
      if (currentScale <= 0 || newScale === currentScale) return;

      const factor = newScale / currentScale;
      const { x, y } = positionRef.current;
      setPosition({ x: x * factor, y: y * factor });
      setScale(newScale);
    },
    [minScale, maxScale, setScale]
  );

  const zoomIn = useCallback(() => {
    if (!enableZoom) return;
    zoomAroundCenter(scaleRef.current * scaleStep);
  }, [enableZoom, scaleStep, zoomAroundCenter]);

  const zoomOut = useCallback(() => {
    if (!enableZoom) return;
    zoomAroundCenter(scaleRef.current / scaleStep);
  }, [enableZoom, scaleStep, zoomAroundCenter]);

  const resetTransform = useCallback(() => {
    setPosition(initialPosition);
    setScale(initialScale);
  }, [initialPosition, initialScale, setScale]);

  // Mouse handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return; // Only left mouse button

      // Check if click is on a button or control element
      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('[role="button"]') ||
        target.closest('.controls')
      ) {
        return;
      }

      setIsDragging(true);
      onDragStart?.();

      const newDragStart = {
        x: e.clientX - positionRef.current.x,
        y: e.clientY - positionRef.current.y,
      };
      setDragStart(newDragStart);
      dragStartRef.current = newDragStart;
    },
    [onDragStart]
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    setPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current) {
      setIsDragging(false);
      onDragEnd?.();
    }
  }, [onDragEnd]);

  // Touch handlers
  const getTouchDistance = (touches: React.TouchList): number => {
    if (touches.length < 2) return 0;
    const touch1 = touches[0];
    const touch2 = touches[1];
    if (!touch1 || !touch2) return 0;
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchMidpoint = (touches: React.TouchList): Position => {
    if (touches.length < 2) return { x: 0, y: 0 };
    const touch1 = touches[0];
    const touch2 = touches[1];
    if (!touch1 || !touch2) return { x: 0, y: 0 };
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // Check if touch is on a button or control element
      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('[role="button"]') ||
        target.closest('.controls')
      ) {
        return;
      }

      // Handle two-finger pinch zoom
      if (e.touches.length === 2 && enableZoom) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (e.touches.length === 1) {
        setIsDragging(true);
        onDragStart?.();

        const touch = e.touches[0];
        if (!touch) return;

        const newDragStart = {
          x: touch.clientX - positionRef.current.x,
          y: touch.clientY - positionRef.current.y,
        };
        setDragStart(newDragStart);
        dragStartRef.current = newDragStart;
      }
    },
    [enableZoom, onDragStart]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      // Handle two-finger pinch zoom + pan
      if (e.touches.length === 2 && enableZoom) {
        e.preventDefault();
        e.stopPropagation();

        const currentDistance = getTouchDistance(e.touches);
        const currentMidpoint = getTouchMidpoint(e.touches);

        // Initialize pinch gesture
        if (pinchStartDistanceRef.current === 0) {
          pinchStartDistanceRef.current = currentDistance;
          pinchStartScaleRef.current = scaleRef.current;
          pinchStartPositionRef.current = positionRef.current;
          pinchMidpointRef.current = currentMidpoint;
          return;
        }

        const container = containerRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const zoomOriginX = pinchMidpointRef.current.x - containerRect.left;
        const zoomOriginY = pinchMidpointRef.current.y - containerRect.top;

        const scaleDelta = currentDistance / pinchStartDistanceRef.current;
        const newScale = Math.min(
          Math.max(pinchStartScaleRef.current * scaleDelta, minScale),
          maxScale
        );

        const imagePointX =
          (zoomOriginX -
            containerRect.width / 2 -
            pinchStartPositionRef.current.x) /
          pinchStartScaleRef.current;
        const imagePointY =
          (zoomOriginY -
            containerRect.height / 2 -
            pinchStartPositionRef.current.y) /
          pinchStartScaleRef.current;

        let newX =
          zoomOriginX - containerRect.width / 2 - imagePointX * newScale;
        let newY =
          zoomOriginY - containerRect.height / 2 - imagePointY * newScale;

        // Add panning based on midpoint movement
        const midpointDeltaX = currentMidpoint.x - pinchMidpointRef.current.x;
        const midpointDeltaY = currentMidpoint.y - pinchMidpointRef.current.y;

        newX += midpointDeltaX;
        newY += midpointDeltaY;

        setScale(newScale);
        setPosition({ x: newX, y: newY });
        return;
      }

      // Single touch dragging
      if (isDraggingRef.current && e.touches.length === 1) {
        const touch = e.touches[0];
        if (!touch) return;
        setPosition({
          x: touch.clientX - dragStartRef.current.x,
          y: touch.clientY - dragStartRef.current.y,
        });
      }
    },
    [enableZoom, minScale, maxScale, setScale]
  );

  const handleTouchEnd = useCallback(() => {
    pinchStartDistanceRef.current = 0;
    pinchStartScaleRef.current = 1;
    pinchStartPositionRef.current = { x: 0, y: 0 };
    pinchMidpointRef.current = { x: 0, y: 0 };

    if (isDraggingRef.current) {
      setIsDragging(false);
      onDragEnd?.();
    }
  }, [onDragEnd]);

  // Global mouse events for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return {
    position,
    scale,
    isDragging,
    containerRef,
    setPosition,
    setScale,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    zoomIn,
    zoomOut,
    resetTransform,
    positionRef,
    scaleRef,
    isDraggingRef,
  };
}
